import { CHUNK_SIZE } from '../constants/translationConfig';
import { chunkArray } from '../utils/chunkArray';

/**
 * Turns a list of targets into translations, asking the API as little as possible:
 *   1. identical texts are sent once,
 *   2. anything already in the saved cache is answered without a request,
 *   3. the rest is sent in chunks of CHUNK_SIZE and saved to the cache.
 *
 * Never throws. If a request fails, the translations found so far are still returned together
 * with the error, so whatever succeeded can still be shown.
 *
 * @returns {Promise<{ translations: Map<string, string>, error: Error|null }>}
 *          keyed by the trimmed original text
 */
export const translateTargets = async ({
    targets,
    targetLanguage,
    cache,
    translateBatch,
    isCancelled = () => false,
}) => {
    const translations = new Map();
    const missing = [];
    const seen = new Set();

    targets.forEach(({ text }) => {
        const key = text.trim();
        if (seen.has(key)) return;
        seen.add(key);
        const cached = cache.get(targetLanguage, key);
        if (cached !== undefined) translations.set(key, cached);
        else missing.push(key);
    });

    for (const chunk of chunkArray(missing, CHUNK_SIZE)) {
        if (isCancelled()) break;
        try {
            const results = await translateBatch(chunk, targetLanguage);
            const toCache = [];
            chunk.forEach((original, index) => {
                const translated = results[index];
                if (typeof translated === 'string' && translated.trim()) {
                    translations.set(original, translated);
                    toCache.push([original, translated]);
                }
            });
            cache.setMany(targetLanguage, toCache);
        } catch (error) {
            return { translations, error };
        }
    }

    return { translations, error: null };
};
